import axiosInstance from "../api.axiosInstance"
import type {
  InitiateSolarConcertApplicationDto,
  SaveSolarConcertOfferDto,
  SolarConcertApplicationStatusDto,
  SolarConcertCustomerContextDto,
  SolarConcertLoanDocumentDelivery,
  SolarConcertOffersDto,
  SolarConcertSellerReadinessDto,
  SubmitSolarConcertApplicationDto,
  UploadSolarConcertDocumentDto,
} from "../dto/solar-concert.dto"
import type {
  SolarContractReadinessDto,
  UpdateSolarContractLanguageDto,
} from "../dto/solar-contract.dto"
import type {
  SolarLightReachDisclosuresDto,
  SolarLightReachDto,
  SubmitSolarLightReachApplicationDto,
} from "../dto/solar-lightreach.dto"
import type {
  SolarComputePreviewDto,
  SolarProposalComputeDto,
  UpdateSolarProposalComputeDto,
} from "../dto/solar-proposal-compute.dto"
import type {
  SolarGreenButtonApplyRequest,
  SolarProposalConsumptionDto,
  SolarProposalGreenButtonApplyDto,
  UpdateSolarProposalConsumptionDto,
} from "../dto/solar-proposal-consumption.dto"
import type { SolarProposalDesignReadDto } from "../dto/solar-proposal-design.dto"
import type {
  SolarDesignerBundleDto,
  SolarDesignSaveBody,
  SolarDesignSaveResultDto,
} from "../dto/solar-proposal-designer.dto"
import type { SolarProposalEquipmentSectionDto } from "../dto/solar-proposal-equipment.dto"
import type {
  SelectSolarLoanProductDto,
  SolarCreditHumanAttemptBody,
  SolarCreditHumanHandoffDto,
  SolarExistingLoanApplicationDto,
  SolarProposalFinancingDto,
} from "../dto/solar-proposal-financing.dto"
import type {
  CreateSolarHdmQuoteDto,
  SolarHdmDto,
  SolarHdmQuoteDto,
} from "../dto/solar-proposal-hdm.dto"
import type { SolarProposalImpactSectionDto } from "../dto/solar-proposal-impact.dto"
import type {
  SolarProposalAddersDto,
  SolarProposalExtendedWarrantiesDto,
  SolarProposalExtendedWarrantySelectionDto,
  SolarProposalIncentivesDto,
  SolarProposalRebatesDto,
  SolarProposalVppProgramsDto,
  UpdateSolarProposalAddersDto,
  UpdateSolarProposalExtendedWarrantyDto,
  UpdateSolarProposalIncentivesDto,
  UpdateSolarProposalRebatesDto,
  UpdateSolarProposalVppProgramsDto,
} from "../dto/solar-proposal-pricing-steps.dto"
import type { SolarProposalPricingDto } from "../dto/solar-proposal-pricing.dto"
import type {
  SolarProductOfferingPreviewDraft,
  SolarProductOfferingPreviewDto,
  SolarProposalProductOfferingDto,
  UpdateSolarProductOfferingDto,
} from "../dto/solar-proposal-product-offering.dto"
import type { SolarProposalSavingsSectionDto } from "../dto/solar-proposal-savings.dto"
import type {
  SolarProposalSummaryDto,
  UpdateSolarProposalMountDto,
} from "../dto/solar-proposal-summary.dto"
import type {
  SolarSystemPricingDto,
  UpdateSolarProposalMarginDto,
} from "../dto/solar-proposal-system-pricing.dto"
import type {
  SolarProposalUtilityDto,
  UpdateSolarProposalUtilityDto,
} from "../dto/solar-proposal-utility.dto"
import type { SolarProposalDto } from "../dto/solar-proposal.dto"
import type {
  SolarSungageApplicationHandoffDto,
  SolarSungageApplicationStateDto,
  StartSolarSungageApplicationDto,
} from "../dto/solar-sungage.dto"

const PROVIDER_WRITE_TIMEOUT_MS = 120_000

/** `.../financing/sungage`: the Sungage application's read and commands. */
const sungagePath = (proposalId: string): string =>
  `/solar-proposal/v2/${encodeURIComponent(proposalId)}/financing/sungage`

/** `.../financing/concert`; the offers routes append `-offers` / `-offer`. */
const concertPath = (proposalId: string): string =>
  `/solar-proposal/v2/${encodeURIComponent(proposalId)}/financing/concert`

/** `.../lightreach`: the LightReach step's read and every command under it. */
const lightReachPath = (proposalId: string): string =>
  `/solar-proposal/v2/${encodeURIComponent(proposalId)}/lightreach`

export class SolarProposalService {
  async getById(proposalId: string): Promise<SolarProposalDto> {
    const response = await axiosInstance.get<SolarProposalDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}`,
    )

    return response.data
  }

  async getSummary(proposalId: string): Promise<SolarProposalSummaryDto> {
    const response = await axiosInstance.get<SolarProposalSummaryDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/summary`,
    )

    return response.data
  }

  async putMount(
    proposalId: string,
    body: UpdateSolarProposalMountDto,
  ): Promise<SolarProposalSummaryDto> {
    const response = await axiosInstance.put<SolarProposalSummaryDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/mount`,
      body,
    )

    return response.data
  }

  async postSummaryConfirm(proposalId: string): Promise<SolarProposalDto> {
    // Bodyless: the gateway checks the stored Summary and records it.
    const response = await axiosInstance.post<SolarProposalDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/summary/confirm`,
    )

    return response.data
  }

  async getDesign(proposalId: string): Promise<SolarProposalDesignReadDto> {
    const response = await axiosInstance.get<SolarProposalDesignReadDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/design`,
    )

    return response.data
  }

  async getDesigner(proposalId: string): Promise<SolarDesignerBundleDto> {
    const response = await axiosInstance.get<SolarDesignerBundleDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/designer`,
    )

    return response.data
  }

  async patchDesign(
    proposalId: string,
    body: SolarDesignSaveBody,
  ): Promise<SolarDesignSaveResultDto> {
    // The save sizes inverters against PowurOS and runs production inline.
    const response = await axiosInstance.patch<SolarDesignSaveResultDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/design`,
      body,
      { timeout: PROVIDER_WRITE_TIMEOUT_MS },
    )

    return response.data
  }

  async postDesignReset(
    proposalId: string,
    expectedUpdatedAt: string,
  ): Promise<SolarDesignSaveResultDto> {
    const response = await axiosInstance.post<SolarDesignSaveResultDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/design/reset`,
      { expectedUpdatedAt },
      { timeout: PROVIDER_WRITE_TIMEOUT_MS },
    )

    return response.data
  }

  async getEquipment(
    proposalId: string,
  ): Promise<SolarProposalEquipmentSectionDto> {
    const response = await axiosInstance.get<SolarProposalEquipmentSectionDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/equipment`,
    )

    return response.data
  }

  async getImpact(proposalId: string): Promise<SolarProposalImpactSectionDto> {
    const response = await axiosInstance.get<SolarProposalImpactSectionDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/impact`,
    )

    return response.data
  }

  async getSavings(proposalId: string): Promise<SolarProposalSavingsSectionDto> {
    const response = await axiosInstance.get<SolarProposalSavingsSectionDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/savings`,
    )

    return response.data
  }

  async getPricing(proposalId: string): Promise<SolarProposalPricingDto> {
    const response = await axiosInstance.get<SolarProposalPricingDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/pricing`,
    )

    return response.data
  }

  async postPricing(proposalId: string): Promise<SolarProposalPricingDto> {
    // Refresh Pricing is a bodyless command; the gateway prices from persisted
    // state only. PowurOS and, on a Concert plan, Concert's offers run inline.
    const response = await axiosInstance.post<SolarProposalPricingDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/pricing`,
      undefined,
      { timeout: PROVIDER_WRITE_TIMEOUT_MS },
    )

    return response.data
  }

  async getSystemPricing(proposalId: string): Promise<SolarSystemPricingDto> {
    const response = await axiosInstance.get<SolarSystemPricingDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/system-pricing`,
    )

    return response.data
  }

  /** Saves the $/W margin only; the stored pricing goes out of date. */
  async putMargin(
    proposalId: string,
    body: UpdateSolarProposalMarginDto,
  ): Promise<SolarSystemPricingDto> {
    const response = await axiosInstance.put<SolarSystemPricingDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/margin`,
      body,
    )

    return response.data
  }

  async postSavings(
    proposalId: string,
  ): Promise<SolarProposalSavingsSectionDto> {
    // Recalculate savings is a bodyless command; the gateway recalculates from
    // persisted state only. It runs several Genability calls inline, so it
    // gets longer than the default timeout rather than reporting a failure
    // while the server is still working.
    const response = await axiosInstance.post<SolarProposalSavingsSectionDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/savings`,
      undefined,
      { timeout: PROVIDER_WRITE_TIMEOUT_MS },
    )

    return response.data
  }

  async getUtility(proposalId: string): Promise<SolarProposalUtilityDto> {
    const response = await axiosInstance.get<SolarProposalUtilityDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/utility`,
    )

    return response.data
  }

  async putUtility(
    proposalId: string,
    body: UpdateSolarProposalUtilityDto,
  ): Promise<SolarProposalUtilityDto> {
    // The save writes Genability tariffs inside a transaction the gateway
    // allows two minutes, and may wait on a running Recalculate's lock, so it
    // gets the same timeout rather than reporting a save that then commits.
    const response = await axiosInstance.put<SolarProposalUtilityDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/utility`,
      body,
      { timeout: PROVIDER_WRITE_TIMEOUT_MS },
    )

    return response.data
  }

  async getConsumption(
    proposalId: string,
  ): Promise<SolarProposalConsumptionDto> {
    const response = await axiosInstance.get<SolarProposalConsumptionDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/consumption`,
    )

    return response.data
  }

  /** Database only at the gateway: nothing is recalculated or queued. */
  async putConsumption(
    proposalId: string,
    body: UpdateSolarProposalConsumptionDto,
  ): Promise<SolarProposalConsumptionDto> {
    const response = await axiosInstance.put<SolarProposalConsumptionDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/consumption`,
      body,
    )

    return response.data
  }

  /**
   * Green Button switch, resume or confirm. Selection only at the gateway:
   * nothing is recalculated or queued, so savings read out of date after.
   */
  async postGreenButton(
    proposalId: string,
    { action, body }: SolarGreenButtonApplyRequest,
  ): Promise<SolarProposalGreenButtonApplyDto> {
    const response = await axiosInstance.post<SolarProposalGreenButtonApplyDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/consumption/green-button/${action}`,
      body,
    )

    return response.data
  }

  async getProductOffering(
    proposalId: string,
  ): Promise<SolarProposalProductOfferingDto> {
    const response = await axiosInstance.get<SolarProposalProductOfferingDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/product-offering`,
    )

    return response.data
  }

  async previewProductOffering(
    proposalId: string,
    draft: SolarProductOfferingPreviewDraft,
    signal?: AbortSignal,
  ): Promise<SolarProductOfferingPreviewDto> {
    const response = await axiosInstance.post<SolarProductOfferingPreviewDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/product-offering/preview`,
      {
        financeType: draft.financeType,
        ...(draft.fundingGroup == null
          ? {}
          : { fundingGroup: draft.fundingGroup }),
        ...(draft.loanProgramId == null
          ? {}
          : { loanProgramId: draft.loanProgramId }),
      },
      { signal },
    )

    return response.data
  }

  async putProductOffering(
    proposalId: string,
    body: UpdateSolarProductOfferingDto,
  ): Promise<SolarProposalProductOfferingDto> {
    // The save runs the constraint checks and production inline, so it gets
    // the provider-write timeout rather than reporting a save that commits.
    const response = await axiosInstance.put<SolarProposalProductOfferingDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/product-offering`,
      body,
      { timeout: PROVIDER_WRITE_TIMEOUT_MS },
    )

    return response.data
  }

  /** The saved AI compute node and the financing lock; no provider call. */
  async getFinancing(proposalId: string): Promise<SolarProposalFinancingDto> {
    const response = await axiosInstance.get<SolarProposalFinancingDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/financing`,
    )

    return response.data
  }

  async putLoanProduct(
    proposalId: string,
    body: SelectSolarLoanProductDto,
  ): Promise<SolarProposalFinancingDto> {
    const response = await axiosInstance.put<SolarProposalFinancingDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/financing/loan-product`,
      body,
    )

    return response.data
  }

  async applyCreditHuman(
    proposalId: string,
    body: SolarCreditHumanAttemptBody,
  ): Promise<SolarCreditHumanHandoffDto> {
    const response = await axiosInstance.post<SolarCreditHumanHandoffDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/financing/credit-human/apply`,
      body,
    )

    return response.data
  }

  async reopenCreditHuman(
    proposalId: string,
    applicationId: string,
  ): Promise<SolarCreditHumanHandoffDto> {
    const response = await axiosInstance.post<SolarCreditHumanHandoffDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/financing/credit-human/reopen`,
      { applicationId },
    )

    return response.data
  }

  async replaceCreditHuman(
    proposalId: string,
    applicationId: string,
    body: SolarCreditHumanAttemptBody,
  ): Promise<SolarCreditHumanHandoffDto> {
    const response = await axiosInstance.post<SolarCreditHumanHandoffDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/financing/credit-human/replace`,
      { applicationId, ...body },
    )

    return response.data
  }

  async markCreditHumanApproved(
    proposalId: string,
    applicationId: string,
  ): Promise<SolarExistingLoanApplicationDto> {
    const response = await axiosInstance.post<SolarExistingLoanApplicationDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/financing/credit-human/mark-approved`,
      { applicationId },
    )

    return response.data
  }

  /** The Concert offers Refresh Pricing stored for today's price; no Concert call. */
  async getConcertOffers(proposalId: string): Promise<SolarConcertOffersDto> {
    const response = await axiosInstance.get<SolarConcertOffersDto>(
      `${concertPath(proposalId)}-offers`,
    )

    return response.data
  }

  /** Choose an offer: Concert refetches for today's price and the Quote is written. */
  async saveConcertOffer(
    proposalId: string,
    body: SaveSolarConcertOfferDto,
  ): Promise<SolarConcertOffersDto> {
    const response = await axiosInstance.put<SolarConcertOffersDto>(
      `${concertPath(proposalId)}-offer`,
      { offerId: body.offerId },
      { timeout: PROVIDER_WRITE_TIMEOUT_MS },
    )

    return response.data
  }

  /** Whether the seller (or mentor) has an onboarded Concert user; a live read. */
  async getConcertSellerReadiness(
    proposalId: string,
  ): Promise<SolarConcertSellerReadinessDto> {
    // Up to two sequential Concert reads: the customer, then its company's users.
    const response = await axiosInstance.get<SolarConcertSellerReadinessDto>(
      `${concertPath(proposalId)}/seller-readiness`,
      { timeout: PROVIDER_WRITE_TIMEOUT_MS },
    )

    return response.data
  }

  /** The application's status, reconciled with Concert; never submits credit. */
  /** The stored Sungage application and Start's gate; no Sungage call. */
  async getSungageApplication(
    proposalId: string,
  ): Promise<SolarSungageApplicationStateDto> {
    const response = await axiosInstance.get<SolarSungageApplicationStateDto>(
      sungagePath(proposalId),
    )

    return response.data
  }

  /** Start (or recover) the Sungage application; answers with the hosted URL. */
  async startSungageApplication(
    proposalId: string,
    body: StartSolarSungageApplicationDto,
  ): Promise<SolarSungageApplicationHandoffDto> {
    const response = await axiosInstance.post<SolarSungageApplicationHandoffDto>(
      `${sungagePath(proposalId)}/start`,
      body,
      { timeout: PROVIDER_WRITE_TIMEOUT_MS },
    )

    return response.data
  }

  /** The current Sungage application's hosted URL again; never a new attempt. */
  async resumeSungageApplication(
    proposalId: string,
    applicationId: string,
  ): Promise<SolarSungageApplicationHandoffDto> {
    const response = await axiosInstance.post<SolarSungageApplicationHandoffDto>(
      `${sungagePath(proposalId)}/resume`,
      { applicationId },
      { timeout: PROVIDER_WRITE_TIMEOUT_MS },
    )

    return response.data
  }

  /** Reconcile the current Sungage application from Sungage. */
  async refreshSungageApplication(
    proposalId: string,
    applicationId: string,
  ): Promise<SolarSungageApplicationStateDto> {
    const response = await axiosInstance.post<SolarSungageApplicationStateDto>(
      `${sungagePath(proposalId)}/refresh`,
      { applicationId },
      { timeout: PROVIDER_WRITE_TIMEOUT_MS },
    )

    return response.data
  }

  async getConcertApplication(
    proposalId: string,
  ): Promise<SolarConcertApplicationStatusDto> {
    // The read reconciles with Concert, which may select and record approved terms.
    const response = await axiosInstance.get<SolarConcertApplicationStatusDto>(
      concertPath(proposalId),
      { timeout: PROVIDER_WRITE_TIMEOUT_MS },
    )

    return response.data
  }

  /** Create or reuse this proposal's Concert customer. Never submits credit. */
  async initiateConcertApplication(
    proposalId: string,
    body: InitiateSolarConcertApplicationDto,
  ): Promise<SolarConcertCustomerContextDto> {
    const response = await axiosInstance.post<SolarConcertCustomerContextDto>(
      `${concertPath(proposalId)}/initiate`,
      body,
      { timeout: PROVIDER_WRITE_TIMEOUT_MS },
    )

    return response.data
  }

  async submitConcertApplication(
    proposalId: string,
    body: SubmitSolarConcertApplicationDto,
  ): Promise<SolarConcertApplicationStatusDto> {
    const response = await axiosInstance.post<SolarConcertApplicationStatusDto>(
      `${concertPath(proposalId)}/in-person`,
      body,
      { timeout: PROVIDER_WRITE_TIMEOUT_MS },
    )

    return response.data
  }

  async uploadConcertDocument(
    proposalId: string,
    stipulationId: string,
    body: UploadSolarConcertDocumentDto,
  ): Promise<SolarConcertApplicationStatusDto> {
    const response = await axiosInstance.post<SolarConcertApplicationStatusDto>(
      `${concertPath(proposalId)}/stipulations/${encodeURIComponent(stipulationId)}/uploads`,
      body,
      { timeout: PROVIDER_WRITE_TIMEOUT_MS },
    )

    return response.data
  }

  async submitConcertDocumentsForReview(
    proposalId: string,
  ): Promise<SolarConcertApplicationStatusDto> {
    const response = await axiosInstance.post<SolarConcertApplicationStatusDto>(
      `${concertPath(proposalId)}/supporting-documents/submit-for-review`,
      undefined,
      { timeout: PROVIDER_WRITE_TIMEOUT_MS },
    )

    return response.data
  }

  /** Ask Concert to build the loan documents for an approved application. */
  async generateConcertLoanDocuments(
    proposalId: string,
  ): Promise<SolarConcertApplicationStatusDto> {
    const response = await axiosInstance.post<SolarConcertApplicationStatusDto>(
      `${concertPath(proposalId)}/loan-documents`,
      undefined,
      { timeout: PROVIDER_WRITE_TIMEOUT_MS },
    )

    return response.data
  }

  /** Send the loan documents (Concert's main contract) for signature. */
  async sendConcertLoanDocuments(
    proposalId: string,
    method: SolarConcertLoanDocumentDelivery = `email`,
  ): Promise<SolarConcertApplicationStatusDto> {
    const response = await axiosInstance.post<SolarConcertApplicationStatusDto>(
      `${concertPath(proposalId)}/loan-documents/send`,
      { method },
      { timeout: PROVIDER_WRITE_TIMEOUT_MS },
    )

    return response.data
  }

  /** The LightReach offers, selection, application and contract; no LightReach call. */
  async getLightReach(proposalId: string): Promise<SolarLightReachDto> {
    const response = await axiosInstance.get<SolarLightReachDto>(
      lightReachPath(proposalId),
    )

    return response.data
  }

  /** Get LightReach pricing: the account, utility and design go to LightReach; offers are stored. */
  async getLightReachPricing(proposalId: string): Promise<SolarLightReachDto> {
    return this.postLightReach(proposalId, `/pricing`)
  }

  /** Choose an offer: its quote, the back-solved margin and one PowurOS price. */
  async selectLightReachOffer(
    proposalId: string,
    offerId: string,
  ): Promise<SolarLightReachDto> {
    return this.postLightReach(proposalId, `/offers/select`, { offerId })
  }

  /** The disclosures LightReach publishes now, for the application's consent. */
  async getLightReachDisclosures(
    proposalId: string,
  ): Promise<SolarLightReachDisclosuresDto> {
    const response = await axiosInstance.get<SolarLightReachDisclosuresDto>(
      `${lightReachPath(proposalId)}/disclosures`,
      { timeout: PROVIDER_WRITE_TIMEOUT_MS },
    )

    return response.data
  }

  async submitLightReachApplication(
    proposalId: string,
    body: SubmitSolarLightReachApplicationDto,
  ): Promise<SolarLightReachDto> {
    return this.postLightReach(proposalId, `/application`, body)
  }

  /** Resolve a held submission and read every application back from LightReach. */
  async refreshLightReachApplication(
    proposalId: string,
  ): Promise<SolarLightReachDto> {
    return this.postLightReach(proposalId, `/application/refresh`)
  }

  /** Apply again after a declined, frozen or expired application, kept as history. */
  async reapplyLightReach(
    proposalId: string,
    body: SubmitSolarLightReachApplicationDto,
  ): Promise<SolarLightReachDto> {
    return this.postLightReach(proposalId, `/application/reapply`, body)
  }

  async sendLightReachContract(proposalId: string): Promise<SolarLightReachDto> {
    return this.postLightReach(proposalId, `/contract/send`)
  }

  async refreshLightReachContract(
    proposalId: string,
  ): Promise<SolarLightReachDto> {
    return this.postLightReach(proposalId, `/contract/refresh`)
  }

  /** Void an unsigned LightReach contract; it stays as history. */
  async voidLightReachContract(proposalId: string): Promise<SolarLightReachDto> {
    return this.postLightReach(proposalId, `/contract/void`)
  }

  async retryLightReachSupplemental(
    proposalId: string,
    attemptId: string,
  ): Promise<SolarLightReachDto> {
    return this.postLightReach(
      proposalId,
      `/contract/supplementals/${encodeURIComponent(attemptId)}/retry`,
    )
  }

  /** Every LightReach command calls LightReach inline and answers with the read. */
  private async postLightReach(
    proposalId: string,
    subpath: string,
    body?: unknown,
  ): Promise<SolarLightReachDto> {
    const response = await axiosInstance.post<SolarLightReachDto>(
      `${lightReachPath(proposalId)}${subpath}`,
      body,
      { timeout: PROVIDER_WRITE_TIMEOUT_MS },
    )

    return response.data
  }

  async getCompute(proposalId: string): Promise<SolarProposalComputeDto> {
    const response = await axiosInstance.get<SolarProposalComputeDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/compute`,
    )

    return response.data
  }

  /** The AI compute nodes PowurOS offers on the saved Finance Program; writes nothing. */
  async previewCompute(
    proposalId: string,
    signal?: AbortSignal,
  ): Promise<SolarComputePreviewDto> {
    const response = await axiosInstance.post<SolarComputePreviewDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/compute/preview`,
      undefined,
      { signal },
    )

    return response.data
  }

  async putCompute(
    proposalId: string,
    body: UpdateSolarProposalComputeDto,
  ): Promise<SolarProposalComputeDto> {
    // The save asks PowurOS what it offers, so it gets the provider timeout.
    const response = await axiosInstance.put<SolarProposalComputeDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/compute`,
      body,
      { timeout: PROVIDER_WRITE_TIMEOUT_MS },
    )

    return response.data
  }

  async getAdders(proposalId: string): Promise<SolarProposalAddersDto> {
    const response = await axiosInstance.get<SolarProposalAddersDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/adders`,
    )

    return response.data
  }

  /** Every listed adder with its draft state; required ones stay on. */
  async patchAdders(
    proposalId: string,
    body: UpdateSolarProposalAddersDto,
  ): Promise<SolarProposalAddersDto> {
    const response = await axiosInstance.patch<SolarProposalAddersDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/adders`,
      body,
    )

    return response.data
  }

  async getRebates(proposalId: string): Promise<SolarProposalRebatesDto> {
    const response = await axiosInstance.get<SolarProposalRebatesDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/rebates`,
    )

    return response.data
  }

  async putRebates(
    proposalId: string,
    body: UpdateSolarProposalRebatesDto,
  ): Promise<SolarProposalRebatesDto> {
    const response = await axiosInstance.put<SolarProposalRebatesDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/rebates`,
      body,
    )

    return response.data
  }

  async getExtendedWarranties(
    proposalId: string,
  ): Promise<SolarProposalExtendedWarrantiesDto> {
    const response = await axiosInstance.get<SolarProposalExtendedWarrantiesDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/extended-warranties`,
    )

    return response.data
  }

  async putExtendedWarranty(
    proposalId: string,
    body: UpdateSolarProposalExtendedWarrantyDto,
  ): Promise<SolarProposalExtendedWarrantySelectionDto> {
    const response =
      await axiosInstance.put<SolarProposalExtendedWarrantySelectionDto>(
        `/solar-proposal/v2/${encodeURIComponent(proposalId)}/extended-warranty`,
        body,
      )

    return response.data
  }

  async getIncentives(proposalId: string): Promise<SolarProposalIncentivesDto> {
    const response = await axiosInstance.get<SolarProposalIncentivesDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/incentives`,
    )

    return response.data
  }

  async putIncentives(
    proposalId: string,
    body: UpdateSolarProposalIncentivesDto,
  ): Promise<SolarProposalIncentivesDto> {
    const response = await axiosInstance.put<SolarProposalIncentivesDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/incentives`,
      body,
    )

    return response.data
  }

  /** Asks the incentive provider again for this home and stored pricing. */
  async refreshIncentives(
    proposalId: string,
  ): Promise<SolarProposalIncentivesDto> {
    const response = await axiosInstance.post<SolarProposalIncentivesDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/incentives/refresh`,
      undefined,
      { timeout: PROVIDER_WRITE_TIMEOUT_MS },
    )

    return response.data
  }

  async getVppPrograms(
    proposalId: string,
  ): Promise<SolarProposalVppProgramsDto> {
    const response = await axiosInstance.get<SolarProposalVppProgramsDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/vpp-programs`,
    )

    return response.data
  }

  async putVppPrograms(
    proposalId: string,
    body: UpdateSolarProposalVppProgramsDto,
  ): Promise<SolarProposalVppProgramsDto> {
    const response = await axiosInstance.put<SolarProposalVppProgramsDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/vpp-programs`,
      body,
    )

    return response.data
  }

  async postProduction(
    proposalId: string,
  ): Promise<SolarProposalProductOfferingDto> {
    const response = await axiosInstance.post<SolarProposalProductOfferingDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/production`,
      undefined,
      { timeout: PROVIDER_WRITE_TIMEOUT_MS },
    )

    return response.data
  }

  /** Contract readiness, from a live Contracting preflight. */
  async getContractReadiness(
    proposalId: string,
  ): Promise<SolarContractReadinessDto> {
    const response = await axiosInstance.get<SolarContractReadinessDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/contract`,
    )

    return response.data
  }

  /** Sends the contract package; answers with the readiness after the send. */
  async sendContract(
    proposalId: string,
    body: { idempotencyKey: string },
  ): Promise<SolarContractReadinessDto> {
    const response = await axiosInstance.post<SolarContractReadinessDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/contract`,
      body,
    )

    return response.data
  }

  async putContractLanguage(
    proposalId: string,
    body: UpdateSolarContractLanguageDto,
  ): Promise<SolarContractReadinessDto> {
    const response = await axiosInstance.put<SolarContractReadinessDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/contract/language`,
      body,
    )

    return response.data
  }

  /** HDM quotes so far and the quote Vision proposes; calls HDM for the draft. */
  async getHdm(proposalId: string): Promise<SolarHdmDto> {
    const response = await axiosInstance.get<SolarHdmDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/hdm`,
      // The read waits on HDM's catalog and eligibility checks and PowurOS.
      { timeout: PROVIDER_WRITE_TIMEOUT_MS },
    )

    return response.data
  }

  /** Creates an HDM quote from the seller's confirmed details; the proposal is unchanged. */
  async createHdmQuote(
    proposalId: string,
    body: CreateSolarHdmQuoteDto,
  ): Promise<SolarHdmQuoteDto> {
    const response = await axiosInstance.post<SolarHdmQuoteDto>(
      `/solar-proposal/v2/${encodeURIComponent(proposalId)}/hdm/quotes`,
      body,
      // Outlasts the gateway's HDM checks and create, so a slow success isn't reported as a failure.
      { timeout: PROVIDER_WRITE_TIMEOUT_MS },
    )

    return response.data
  }
}

export const solarProposalService = new SolarProposalService()
